import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { Textarea } from '../../components/ui/textarea';
import { 
  ArrowLeft, MapPin, Calendar, Package, Truck, User, Mail, Phone, 
  Download, FileText, AlertCircle, Shield, Clock, Activity, 
  MessageSquare, Edit, DollarSign, TrendingUp, History, CheckCircle2, Handshake, Send, RefreshCw,
  Plus, Play, XCircle, Eye, Loader2, Users, Pencil, Info, StickyNote
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import usePermissions from '../../contexts/PermissionsContext';
import { PERMS } from '../../utils/permissions';
import TransportMap from '../../components/TransportMap';
import StatusTimelineSimplified from '../../components/StatusTimelineSimplified';
import DocumentsChecklist from '../../components/DocumentsChecklist';
import MessagingSystem from '../../components/MessagingSystem';
import NegotiationPanelV2 from '../../components/NegotiationPanelV2';
import PlanificationModal from '../../components/PlanificationModal';
import ChargementModal from '../../components/ChargementModal';
import EnRouteModal from '../../components/EnRouteModal';
import LivraisonModal from '../../components/LivraisonModal';
import FacturationModal from '../../components/FacturationModal';
import AdvancesManager from '../../components/AdvancesManager';
import PaymentModal from '../../components/PaymentModal';
import WorkflowPanel from '../../components/WorkflowPanel';
import OperatorAssignmentModal from '../../components/OperatorAssignmentModal';
import FieldOpsAssignmentModal from '../../components/FieldOpsAssignmentModal';
import CreateDetailedQuoteModal from '../../components/CreateDetailedQuoteModal';
import LoadingNoteModal from '../../components/operations/LoadingNoteModal';
import LoadingNoteWizardModal from '../../components/operations/LoadingNoteWizardModal';
import LoadingNotesV3Panel from '../../components/operations/LoadingNotesV3Panel';
import ExternalSignCodeBlock from '../../components/operations/ExternalSignCodeBlock';
import InternalNotesTab from '../../components/notes/InternalNotesTab';
import RegenerateInvoiceModal from '../../components/RegenerateInvoiceModal';
import InvoiceGenerationModal from '../../components/InvoiceGenerationModal';
import PaymentReversalModal from '../../components/PaymentReversalModal';
import FneCertifyButton from '../../components/FneCertifyButton';
import FneCertifyModal from '../../components/FneCertifyModal';
import { FneBillingCard } from '../../components/operation/FneBillingCard';
import { FneExemptionDialog } from '../../components/operation/FneExemptionDialog';
import { FneCreditNoteDialog } from '../../components/operation/FneCreditNoteDialog';
import { CollapsibleCard } from '../../components/ui/CollapsibleCard';
import FneLockBadge, { isFneLocked as _isFneLocked } from '../../components/FneLockBadge';
import FneRccmMismatchBanner from '../../components/FneRccmMismatchBanner';
import ClientInfoEditDialog from '../../components/ClientInfoEditDialog';
import ReassignClientDialog from '../../components/ReassignClientDialog';
import FneBillingClientCard from '../../components/FneBillingClientCard';
import EditOperationModal from '../../components/EditOperationModal';
import { getVehicleTypeLabel } from '../../constants/vehicleTypes';
import HistoriqueModal from '../../components/HistoriqueModal';
import { FinancialStatusBadge } from '../../components/FinancialStatusBadge';
import WhatsAppNotificationPanel from '../../components/WhatsAppNotificationPanel';
import AdminFinancialSection from '../../components/operation/AdminFinancialSection';
import OperationMarginBanner from '../../components/operation/OperationMarginBanner';
import WavePaymentLinkModal from '../../components/WavePaymentLinkModal';
import { AdminCompactWorkflow } from '../../components/workflow/CompactDualWorkflow';
import ActionsAdministratives from '../../components/workflow/ActionsAdministratives';
import StickyHeader from '../../components/operation/StickyHeader';
import LogisticBlock from '../../components/operation/LogisticBlock';
import SidebarRight from '../../components/operation/SidebarRight';
import IncidentsPanel from '../../components/IncidentsPanel';
import GPSUpdatePanel from '../../components/GPSUpdatePanel';
import OperationOCRDocuments from '../../components/OperationOCRDocuments';
import { useOperationDocuments } from '../../hooks/useOperationDocuments';
import DocumentsComplianceCenter from '../../components/operations/documents/DocumentsComplianceCenter';
import DocumentsComplianceRail from '../../components/operations/documents/DocumentsComplianceRail';
import AddDocumentModal from '../../components/operations/documents/AddDocumentModal';
import RequestFieldModal from '../../components/operations/documents/RequestFieldModal';
import axios from 'axios';
import ExternalRefField from '../../components/common/ExternalRefField';
import { toast } from '../../hooks/use-toast';
import { getStatusBadge as getStatusConfig, getStatusLabel } from '../../constants/statuses';
import { useAuth } from '../../contexts/AuthContext';
import { formatVehicleType, formatOperationType, formatMerchandiseType } from '../../utils/formatters';
import { logger } from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminOperationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [operation, setOperation] = useState(null);
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [reassignClientOpen, setReassignClientOpen] = useState(false);
  const [showFneCertifyModal, setShowFneCertifyModal] = useState(false);
  const [showFneExemptionDialog, setShowFneExemptionDialog] = useState(false);
  const [fneExemptMode, setFneExemptMode] = useState('exempt');
  const [showCreditNoteDialog, setShowCreditNoteDialog] = useState(false);
  const [creditNotes, setCreditNotes] = useState([]);
  const [internalNotesCount, setInternalNotesCount] = useState(0);

  // Onglet Documents — centre de conformité
  const [activeTab, setActiveTab] = useState('timeline');
  const docData = useOperationDocuments(id);
  const [addDocState, setAddDocState] = useState({ open: false, type: 'autre', files: [] });
  const [reqFieldState, setReqFieldState] = useState({ open: false, labels: [] });
  const openAddDoc = (type = 'autre', files = []) => setAddDocState({ open: true, type, files: files ? Array.from(files) : [] });
  const openRequestField = (labels = []) => setReqFieldState({ open: true, labels: labels && labels.length ? labels : docData.stats.manquantsList });

  const [previousStatus, setPreviousStatus] = useState(null); // Pour détecter les changements
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false); // Indicateur de synchronisation en cours
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [allowedStatuses, setAllowedStatuses] = useState([]);
  const [availableActions, setAvailableActions] = useState([]);
  const [showActionsPanel, setShowActionsPanel] = useState(false);
  const [showHistoriqueModal, setShowHistoriqueModal] = useState(false);
  const [showWavePaymentModal, setShowWavePaymentModal] = useState(false);
  
  // Modals state
  const [showPlanificationModal, setShowPlanificationModal] = useState(false);
  const [showChargementModal, setShowChargementModal] = useState(false);
  const [showEnRouteModal, setShowEnRouteModal] = useState(false);
  const [showLivraisonModal, setShowLivraisonModal] = useState(false);
  const [showFacturationModal, setShowFacturationModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReversePaymentModal, setShowReversePaymentModal] = useState(false);
  const [showOperatorModal, setShowOperatorModal] = useState(false);
  const [showFieldOpsModal, setShowFieldOpsModal] = useState(false);
  const [showCreateQuoteModal, setShowCreateQuoteModal] = useState(false);
  const [showReviseQuoteModal, setShowReviseQuoteModal] = useState(false);
  const [showRegenerateInvoiceModal, setShowRegenerateInvoiceModal] = useState(false);
  // PM Spec 17/02/2026 — Acomptes
  const [showAdvancesModal, setShowAdvancesModal] = useState(false);
  const [showInvoiceGenerationModal, setShowInvoiceGenerationModal] = useState(false);
  const [invoiceGenMode, setInvoiceGenMode] = useState('invoice');
  const [showEditOperationModal, setShowEditOperationModal] = useState(false);
  // Bons de chargement / déchargement (PM Spec 30/04/2026)
  const [loadingNoteModal, setLoadingNoteModal] = useState({ open: false, type: null });
  const [loadingNotesStatus, setLoadingNotesStatus] = useState(null);
  
  // Financial correction state
  const [showFinancialEditDialog, setShowFinancialEditDialog] = useState(false);
  const [showRetroStatusDialog, setShowRetroStatusDialog] = useState(false);
  const [retroTarget, setRetroTarget] = useState('');
  const [correctionMotif, setCorrectionMotif] = useState('');
  const [financialEdit, setFinancialEdit] = useState({});
  const [financialCorrections, setFinancialCorrections] = useState([]);
  const [savingCorrection, setSavingCorrection] = useState(false);

  // Date editing state
  const [editingDates, setEditingDates] = useState(false);
  const [dateEdits, setDateEdits] = useState({});
  const [dateMotif, setDateMotif] = useState('');
  const [savingDates, setSavingDates] = useState(false);

  // Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Child Operations (Transferts) states
  const [childOperations, setChildOperations] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [creatingChild, setCreatingChild] = useState(false);
  const [childForm, setChildForm] = useState({
    date: new Date().toISOString().split('T')[0],
    shift: 'day',
    plannedStartTime: '07:00',
    plannedEndTime: '17:00',
    useParentVehicle: true,
    useParentDriver: true,
    overrideVehicleId: '',
    overrideDriverId: ''
  });
  const [parentSummary, setParentSummary] = useState(null);
  // Feature: afficher la facturation dans le panneau droit
  const [operationInvoices, setOperationInvoices] = useState([]);
  const [showInvoicesHistory, setShowInvoicesHistory] = useState(false);

  const fetchOperationInvoices = async (opId) => {
    try {
      const resp = await axios.get(`${API}/documents/operation/${opId}`, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      const invoices = (resp.data?.documents || []).filter(d => d.document_type === 'invoice');
      setOperationInvoices(invoices);
    } catch (err) {
      // Silently ignore - badge will just show financial status without invoice link
      setOperationInvoices([]);
    }
  };

  const fetchCreditNotes = async () => {
    try {
      const resp = await axios.get(`${API}/fne/credit-notes/by-operation/${id}`);
      setCreditNotes(resp.data?.items || []);
    } catch (err) {
      setCreditNotes([]);
    }
  };

  useEffect(() => {
    fetchOperation();
    fetchCorrections();
    fetchCreditNotes();
    
    // Auto-refresh every 2 seconds for near real-time updates
    const intervalId = setInterval(() => {
      fetchOperation();
    }, 2000); // Rafraîchissement toutes les 2 secondes (plus réactif)
    
    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [id]);

  // Fetch child operations when operation is loaded
  useEffect(() => {
    if (operation) {
      fetchChildOperations();
      fetchOperationInvoices(operation.id);
      fetchLoadingNotesStatus(operation.id);
    }
  }, [operation?.id]);

  const fetchLoadingNotesStatus = async (opId) => {
    try {
      const r = await axios.get(`${API}/operations/${opId}/loading-notes/_status`);
      setLoadingNotesStatus(r.data);
    } catch (e) { /* silent */ }
  };

  const fetchOperation = async () => {
    try {
      setSyncing(true);
      const response = await axios.get(`${API}/transport-requests/${id}`, {
        headers: {
          'Authorization': 'Bearer cookie-auth'
        }
      });
      
      const newOperation = response.data;
      
      // Auto-redirect if this is a contract run (not a regular operation)
      if (newOperation.isContractRun && newOperation.contractId) {
        navigate(`/admin/contracts/${newOperation.contractId}/runs/${newOperation.id}`, { replace: true });
        return;
      }
      
      // Détecter changement de statut et notifier
      if (previousStatus && previousStatus !== newOperation.status) {
        toast({
          title: "🔄 Statut mis à jour",
          description: `L'opération est passée de "${getStatusLabel(previousStatus)}" à "${getStatusLabel(newOperation.status)}"`,
          duration: 4000
        });
      }
      
      setPreviousStatus(newOperation.status);
      setOperation(newOperation);
      setSyncing(false);
      
      // Fetch available actions using new workflow endpoint
      try {
        const actionsResponse = await axios.get(`${API}/operation-status/${id}/available-actions`, {
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        });
        setAvailableActions(actionsResponse.data.available_actions || []);
        
        // Keep backward compatibility: convert actions to old format for Select
        const legacyStatuses = actionsResponse.data.available_actions?.map(action => ({
          value: action.status,
          label: action.label
        })) || [];
        setAllowedStatuses(legacyStatuses);
      } catch (err) {
        console.error('Error fetching available actions:', err);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching operation:', error);
      
      // Check if it's a 404 (operation not found)
      if (error.response?.status === 404) {
        toast({
          title: "Opération introuvable",
          description: "Cette opération n'existe pas. Redirection...",
          variant: "destructive"
        });
        // Redirect to operations list after 2 seconds
        setTimeout(() => navigate('/admin/operations'), 2000);
      } else {
        toast({
          title: "Erreur",
          description: "Impossible de charger l'opération",
          variant: "destructive"
        });
      }
      
      setLoading(false);
    }
  };


  // Fetch financial corrections history
  const fetchCorrections = async () => {
    try {
      const res = await axios.get(`${API}/admin/operations/${id}/financial-corrections`, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      setFinancialCorrections(res.data.corrections || []);
    } catch (e) { console.error('Error fetching corrections:', e); }
  };

  // Handle retro-status (payee → en_attente_paiement / livree)
  const handleRetroStatus = async () => {
    if (!correctionMotif || correctionMotif.trim().length < 5) {
      toast({ title: "Erreur", description: "Motif obligatoire (min 5 caractères)", variant: "destructive" });
      return;
    }
    setSavingCorrection(true);
    try {
      await axios.post(`${API}/operation-status/${id}/change`, {
        new_status: retroTarget,
        reason: correctionMotif,
        validate_requirements: false
      }, { headers: { 'Authorization': 'Bearer cookie-auth' } });
      toast({ title: "Statut corrigé", description: `Opération ramenée à "${getStatusLabel(retroTarget)}"` });
      setShowRetroStatusDialog(false);
      setCorrectionMotif('');
      fetchOperation();
      fetchCorrections();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors du changement de statut";
      toast({ title: "Erreur", description: typeof msg === 'string' ? msg : JSON.stringify(msg), variant: "destructive" });
    }
    setSavingCorrection(false);
  };

  // Handle financial details edit
  const handleFinancialEdit = async () => {
    if (!correctionMotif || correctionMotif.trim().length < 5) {
      toast({ title: "Erreur", description: "Motif obligatoire (min 5 caractères)", variant: "destructive" });
      return;
    }
    setSavingCorrection(true);
    try {
      await axios.patch(`${API}/admin/operations/${id}/financial-details`, {
        ...financialEdit,
        motif: correctionMotif,
      }, { headers: { 'Authorization': 'Bearer cookie-auth' } });
      toast({ title: "Détails financiers mis à jour", description: "Les modifications ont été enregistrées" });
      setShowFinancialEditDialog(false);
      setCorrectionMotif('');
      setFinancialEdit({});
      fetchOperation();
      fetchCorrections();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors de la modification";
      toast({ title: "Erreur", description: typeof msg === 'string' ? msg : JSON.stringify(msg), variant: "destructive" });
    }
    setSavingCorrection(false);
  };

  // Handle date correction
  const handleDateSave = async () => {
    if (!dateMotif || dateMotif.trim().length < 5) {
      toast({ title: "Erreur", description: "Motif obligatoire (min 5 caractères)", variant: "destructive" });
      return;
    }
    // Compare dateEdits against original values to find actual changes
    const DATE_FIELDS = [
      { field: 'deliveryDate', fallbacks: ['plannedArrivalDate', 'arrivalDate', 'desiredDeliveryDate'] },
      { field: 'departureDate', fallbacks: ['plannedDepartureDate', 'desiredPickupDate'] },
      { field: 'invoiceGeneratedAt' },
      { field: 'invoiceDepositDate' },
      { field: 'paymentReceivedAt' },
      { field: 'createdAt' },
    ];
    const changedFields = {};
    DATE_FIELDS.forEach(({ field, fallbacks }) => {
      const originalVal = operation[field] || (fallbacks && fallbacks.reduce((v, f) => v || operation[f], null));
      const originalDate = toInputDate(originalVal);
      const newDate = dateEdits[field] || '';
      if (newDate && newDate !== originalDate) {
        changedFields[field] = newDate;
      }
    });
    if (Object.keys(changedFields).length === 0) {
      toast({ title: "Info", description: "Aucune date modifiée" });
      return;
    }
    setSavingDates(true);
    try {
      await axios.patch(`${API}/admin/operations/${id}/dates`, {
        ...changedFields,
        motif: dateMotif,
      }, { headers: { 'Authorization': 'Bearer cookie-auth' } });
      toast({ title: "Dates mises à jour", description: `${Object.keys(changedFields).length} date(s) modifiée(s)` });
      setEditingDates(false);
      setDateEdits({});
      setDateMotif('');
      fetchOperation();
      fetchCorrections();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors de la modification des dates";
      toast({ title: "Erreur", description: typeof msg === 'string' ? msg : JSON.stringify(msg), variant: "destructive" });
    }
    setSavingDates(false);
  };

  // Helper to format date for input
  const toInputDate = (val) => {
    if (!val) return '';
    try {
      const d = new Date(val);
      return d.toISOString().split('T')[0];
    } catch { return ''; }
  };


  // Fetch child operations (transferts enfants)
  const fetchChildOperations = async () => {
    if (!id) return;
    setLoadingChildren(true);
    try {
      // Get summary first
      const summaryRes = await axios.get(`${API}/operations/${id}/summary`, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      setParentSummary(summaryRes.data);
      
      // Get children list
      const childrenRes = await axios.get(`${API}/operations/${id}/children`, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      setChildOperations(childrenRes.data.items || []);
    } catch (error) {
      console.error('Error fetching child operations:', error);
    } finally {
      setLoadingChildren(false);
    }
  };

  // Create child operation
  const handleCreateChild = async () => {
    if (!childForm.date) {
      toast({ title: "Erreur", description: "Veuillez sélectionner une date", variant: "destructive" });
      return;
    }
    
    setCreatingChild(true);
    try {
      await axios.post(`${API}/operations/${id}/children`, {
        date: childForm.date,
        shift: childForm.shift,
        plannedStartTime: childForm.plannedStartTime,
        plannedEndTime: childForm.plannedEndTime,
        useParentVehicle: childForm.useParentVehicle,
        useParentDriver: childForm.useParentDriver,
        overrideVehicleId: childForm.useParentVehicle ? null : childForm.overrideVehicleId,
        overrideDriverId: childForm.useParentDriver ? null : childForm.overrideDriverId
      }, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      
      toast({ title: "Succès", description: "Transfert créé avec succès" });
      setShowAddChildModal(false);
      setChildForm({
        date: new Date().toISOString().split('T')[0],
        shift: 'day',
        plannedStartTime: '07:00',
        plannedEndTime: '17:00',
        useParentVehicle: true,
        useParentDriver: true,
        overrideVehicleId: '',
        overrideDriverId: ''
      });
      fetchChildOperations();
      fetchOperation(); // Refresh parent to update counters
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = "Erreur lors de la création";
      if (detail) {
        if (typeof detail === 'string') errorMessage = detail;
        else if (Array.isArray(detail)) errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        else if (typeof detail === 'object' && detail.msg) errorMessage = detail.msg;
      }
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setCreatingChild(false);
    }
  };

  // Delete child operation
  const handleDeleteChild = async (childId) => {
    if (!window.confirm('Supprimer ce transfert ?')) return;
    
    try {
      await axios.delete(`${API}/operations/${id}/children/${childId}`, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      toast({ title: "Succès", description: "Transfert supprimé" });
      fetchChildOperations();
      fetchOperation();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = "Erreur lors de la suppression";
      if (detail) {
        if (typeof detail === 'string') errorMessage = detail;
        else if (Array.isArray(detail)) errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        else if (typeof detail === 'object' && detail.msg) errorMessage = detail.msg;
      }
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  // Format date helper
  const formatChildDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Get child status badge
  const getChildStatusBadge = (status) => {
    const config = {
      planned: { label: 'Planifié', dotColor: 'bg-gray-400' },
      in_progress: { label: 'En cours', dotColor: 'bg-amber-500' },
      completed: { label: 'Terminé', dotColor: 'bg-green-500' },
      cancelled: { label: 'Annulé', dotColor: 'bg-gray-300' }
    };
    const c = config[status] || config.planned;
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
        <span className={`w-2 h-2 rounded-full ${c.dotColor}`} />
        {c.label}
      </span>
    );
  };

  // Check if user can add children (RBAC V2 — permission-driven)
  const { hasPermission } = usePermissions();
  const canAddChildren = hasPermission(PERMS.OPERATIONS_CHILDREN_MANAGE) || (user && ['admin', 'operator', 'fieldops', 'ops', 'lead_ops', 'field_ops', 'lead_field_ops'].includes(user.role));
  const canDeleteChildren = hasPermission(PERMS.OPERATIONS_CHILDREN_MANAGE) || (user && ['admin', 'operator', 'ops', 'lead_ops'].includes(user.role));
  // RBAC V2.5 — Finance fournisseur (marge / tarif transporteur) réservée aux rôles finance complète.
  // Le pôle Ops (ops, field_ops, lead_ops, lead_field_ops) ne doit JAMAIS voir la marge.
  const canSeeSupplierFinance = ['admin', 'finance', 'commercial', 'lead_commercial', 'auditeur'].includes((user?.role || '').toLowerCase());
  // Avoir FNE — même règle que le backend (finance.fne.refund + rôles de secours)
  const canRefundFne = hasPermission('finance.fne.refund')
    || ['admin', 'super_admin', 'finance', 'lead_commercial'].includes((user?.role || '').toLowerCase());

  // Modal "transition inhabituelle" (soft-block backend renvoie 409)
  const [unusualTransitionModal, setUnusualTransitionModal] = useState(null);
  // Shape: { newStatus, warning, currentStatus, financialStatus, reason }

  const handleStatusChange = async (newStatus, requiresModal = null, reason = null, confirmUnusual = false) => {
    // Check if cancellation needs a reason modal
    const cancelStatuses = ['annulee_admin', 'annulee_par_client'];
    if (cancelStatuses.includes(newStatus) && !reason) {
      setShowCancelModal(true);
      setCancelReason('');
      return;
    }

    // Check if this status requires a modal
    const modalStatuses = {
      'planifiee': setShowPlanificationModal,
      'en_cours_de_chargement': setShowChargementModal,
      'charge_en_route': setShowEnRouteModal,
      'livree': setShowLivraisonModal,
      'en_attente_paiement': setShowFacturationModal
    };
    
    // If requiresModal is explicitly provided (from new API), use it
    // Otherwise, check if status is in modalStatuses dict (old behavior)
    const needsModal = requiresModal !== null ? requiresModal : !!modalStatuses[newStatus];
    
    if (needsModal && modalStatuses[newStatus]) {
      // Open the appropriate modal
      modalStatuses[newStatus](true);
      return;
    }
    
    // For other statuses, change directly using new endpoint
    setUpdatingStatus(true);
    try {
      // Statuts qui ne nécessitent pas de validation stricte des prérequis
      // (les documents et infos logistiques sont optionnels pour ces transitions)
      const skipValidationStatuses = [
        'prete_au_chargement',  // L'opérateur peut valider même sans tous les documents
        'payee',                // Confirmation de paiement
        'terminee',             // Clôture
        'annulee_admin'         // Annulation admin
      ];
      
      const shouldSkipValidation = skipValidationStatuses.includes(newStatus);
      
      await axios.post(
        `${API}/operation-status/${id}/change`, 
        {
          new_status: newStatus,
          validate_requirements: !shouldSkipValidation,
          confirm_unusual_transition: confirmUnusual,
          ...(reason ? { reason } : {})
        },
        {
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        }
      );
      
      toast({
        title: "✅ Statut mis à jour",
        description: `Le statut a été changé en ${getStatusLabel(newStatus)}`
      });
      
      fetchOperation();
    } catch (error) {
      // Soft-block "transition inhabituelle" → afficher modale de confirmation
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.requires_confirmation) {
        setUnusualTransitionModal({
          newStatus,
          reason,
          warning: detail.warning,
          currentStatus: detail.currentStatus,
          financialStatus: detail.financialStatus,
        });
        setUpdatingStatus(false);
        return;
      }
      console.error('Error updating status:', error);
      // Handle Pydantic validation errors
      let errorMessage = "Impossible de mettre à jour le statut";
      if (detail) {
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
      }
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  const handleModalSuccess = () => {
    fetchOperation();
  };

  const generatePDF = async (type) => {
    try {
      toast({
        title: "⏳ Génération en cours...",
        description: `Génération du ${type === 'quote' ? 'devis' : type === 'invoice' ? 'facture' : 'CMR'}...`
      });

      const response = await axios.post(
        `${API}/documents/generate/${type}/${id}`,
        {},
        { 
          responseType: 'blob',
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const docType = type === 'quote' ? 'Devis' : type === 'invoice' ? 'Facture' : 'CMR';
      link.setAttribute('download', `${docType}_${operation.operationNumber || operation.id.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: "✅ Document généré",
        description: `Le ${docType.toLowerCase()} a été téléchargé avec succès`
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      const detail = error.response?.data?.detail;
      let errorMessage = "Impossible de générer le document";
      if (detail) {
        if (typeof detail === 'string') errorMessage = detail;
        else if (Array.isArray(detail)) errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        else if (typeof detail === 'object' && detail.msg) errorMessage = detail.msg;
      }
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  // Générer la facture depuis un devis accepté et mettre à jour le statut
  const generateInvoiceFromQuote = async () => {
    try {
      toast({
        title: "⏳ Génération de la facture...",
        description: "La facture est en cours de création à partir du devis accepté"
      });

      // Appeler l'endpoint spécifique qui génère la facture et met à jour le statut
      const response = await axios.post(
        `${API}/documents/generate/invoice-from-quote/${id}`,
        {},
        { 
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        }
      );

      if (response.data.success) {
        toast({
          title: "✅ Facture générée",
          description: `Facture ${response.data.invoice_number} créée avec succès. Statut mis à jour.`
        });

        // Télécharger la facture
        try {
          const downloadResponse = await axios.get(
            `${API}/documents/download/${response.data.invoice_number}`,
            { 
              responseType: 'blob',
              headers: {
                'Authorization': 'Bearer cookie-auth'
              }
            }
          );
          
          const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Facture_${response.data.invoice_number}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
        } catch (dlError) {
          console.error('Error downloading invoice:', dlError);
        }

        // Rafraîchir les données de l'opération
        fetchOperation();
      }
    } catch (error) {
      console.error('Error generating invoice from quote:', error);
      const detail = error.response?.data?.detail;
      let errorMessage = "Impossible de générer la facture";
      if (detail) {
        if (typeof detail === 'string') errorMessage = detail;
        else if (Array.isArray(detail)) errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        else if (typeof detail === 'object' && detail.msg) errorMessage = detail.msg;
      }
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status) => {
    const config = getStatusConfig(status);
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
        <span className={`w-2.5 h-2.5 rounded-full ${config.dotColor || 'bg-gray-400'}`} />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateTimeSinceCreation = () => {
    if (!operation) return '';
    const now = new Date();
    const created = new Date(operation.createdAt);
    const diffHours = Math.floor((now - created) / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    return `${diffHours} heure${diffHours > 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1db584]"></div>
      </div>
    );
  }

  if (!operation) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Opération non trouvée</p>
        <Button onClick={() => navigate('/admin/operations')} className="mt-4">
          Retour aux opérations
        </Button>
      </div>
    );
  }

  const formatPrice = (price) => {
    return price ? `${price.toLocaleString('fr-FR')} XOF` : 'N/A';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <StickyHeader
        operation={operation}
        onBack={() => navigate(-1)}
        getStatusBadge={getStatusBadge}
        formatPrice={formatPrice}
      />

      {/* 🔒 FNE Lock banner — affiché si l'opération est verrouillée FNE */}
      {_isFneLocked(operation.fneCertification) && (
        <div className="container mx-auto px-4 pt-3" data-testid="fne-lock-banner">
          <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-md flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <FneLockBadge certification={operation.fneCertification} size="md" />
              <p className="text-xs text-amber-900">
                Cette opération est <strong>verrouillée fiscalement</strong>. Toute modification est refusée (HTTP 423). Pour corriger, émettez un <strong>avoir FNE</strong>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ⚠️ Banner RCCM/NCC mismatch — DGI vs interne */}
      <FneRccmMismatchBanner
        operationId={operation.id}
        targetType="spot"
        companyName={operation.companyName}
        visible={operation.fneCertification?.status === 'certified'
          || operation.fneCertification?.status === 'refunded_partial'
          || operation.fneCertification?.status === 'refunded_total'}
      />

      {/* Timeline des Workflows (Commercial + Opération + Finance) - Compact */}
      <div className="container mx-auto px-4 py-4">
        <AdminCompactWorkflow 
          operation={operation}
          onStageClick={(stage) => {
            logger.debug('Stage clicked:', stage);
          }}
        />
      </div>

      {/* Main Layout: 2 Columns */}
      <div className="container mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Main Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Logistic Block: Map + Operation Details */}
            <LogisticBlock
              operation={operation}
              formatVehicleType={formatVehicleType}
              formatOperationType={formatOperationType}
              formatMerchandiseType={formatMerchandiseType}
              formatDate={formatDate}
            />

            {/* Tabs Section: Timeline, Workflow, Documents, etc. */}
            <Card>
              <CardContent className="pt-6">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  {/* Conteneur avec gradient fade droite pour indiquer le scroll horizontal */}
                  <div className="relative">
                    <TabsList
                      className="flex w-full justify-start overflow-x-auto overflow-y-hidden gap-1 px-1 h-auto py-1 [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent scroll-smooth"
                      data-testid="admin-op-tabs"
                    >
                      <TabsTrigger value="timeline" className="shrink-0 whitespace-nowrap px-3">
                        <Clock className="w-4 h-4 mr-1" />
                        Timeline
                      </TabsTrigger>
                      <TabsTrigger value="negotiation" className="shrink-0 whitespace-nowrap px-3">
                        <Handshake className="w-4 h-4 mr-1" />
                        Négociation
                      </TabsTrigger>
                      <TabsTrigger value="workflow" className="shrink-0 whitespace-nowrap px-3">
                        <DollarSign className="w-4 h-4 mr-1" />
                        Devis & Facture
                      </TabsTrigger>
                      <TabsTrigger value="logistics" className="shrink-0 whitespace-nowrap px-3">
                        <Truck className="w-4 h-4 mr-1" />
                        Logistique
                      </TabsTrigger>
                      <TabsTrigger value="transfers" className="shrink-0 whitespace-nowrap px-3">
                        <Users className="w-4 h-4 mr-1" />
                        Transferts
                        {(operation?.childrenCount > 0 || parentSummary?.childrenSummary?.total > 0) && (
                          <span className="ml-1 text-xs bg-blue-500 text-white rounded-full px-1.5">
                            {operation?.childrenCount || parentSummary?.childrenSummary?.total || 0}
                          </span>
                        )}
                      </TabsTrigger>
                      <TabsTrigger value="documents" className="shrink-0 whitespace-nowrap px-3">
                        <FileText className="w-4 h-4 mr-1" />
                        Documents
                      </TabsTrigger>
                      <TabsTrigger value="bons" data-testid="tab-bons-signatures" className="shrink-0 whitespace-nowrap px-3">
                        <Shield className="w-4 h-4 mr-1" />
                        Bons &amp; signatures
                      </TabsTrigger>
                      <TabsTrigger value="incidents" className="shrink-0 whitespace-nowrap px-3">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        Incidents
                        {operation?.has_incident && (
                          <span className="ml-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        )}
                      </TabsTrigger>
                      <TabsTrigger value="messages" className="shrink-0 whitespace-nowrap px-3">
                        <MessageSquare className="w-4 h-4 mr-1" />
                        Messages
                      </TabsTrigger>
                      <TabsTrigger value="notes" data-testid="tab-internal-notes" className="shrink-0 whitespace-nowrap px-3">
                        <StickyNote className="w-4 h-4 mr-1" />
                        Notes
                        {internalNotesCount > 0 && (
                          <span className="ml-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] rounded-full font-semibold"
                                data-testid="internal-notes-count">
                            {internalNotesCount}
                          </span>
                        )}
                      </TabsTrigger>
                    </TabsList>
                    {/* Indicateur visuel de scroll possible à droite */}
                    <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-white to-transparent" />
                  </div>
                  
                  <TabsContent value="timeline" className="mt-6">
                    <div className="space-y-4">
                      <StatusTimelineSimplified 
                        currentStatus={operation.status}
                        statusHistory={{
                          pending: operation.createdAt,
                        }}
                      />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="negotiation" className="mt-6">
                    <NegotiationPanelV2
                      operation={operation}
                      onUpdate={fetchOperation}
                    />
                  </TabsContent>
                  
                  <TabsContent value="workflow" className="mt-6">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <button
                          onClick={() => generatePDF('quote')}
                          className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                        >
                          <FileText className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-gray-700">Générer Devis</span>
                        </button>
                        <button
                          onClick={() => {
                            setInvoiceGenMode('invoice');
                            setShowInvoiceGenerationModal(true);
                          }}
                          className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
                          data-testid="generate-invoice-workflow-btn"
                        >
                          <DollarSign className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-gray-700">Générer Facture</span>
                        </button>
                        {/* Module 3 — Bouton Générer Facture FNE (visible si client.fneBillingEnabled=true) */}
                        <FneCertifyButton
                          targetType="spot"
                          targetId={operation.id}
                          certification={operation.fneCertification}
                          clientFneEnabled={operation.clientFneBillingEnabled}
                          variant="workflow-card"
                          onSuccess={fetchOperation}
                        />
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="logistics" className="mt-6">
                    <div className="space-y-4">
                      {/* Panneau de mise à jour GPS */}
                      <GPSUpdatePanel 
                        operation={operation}
                        onLocationUpdate={(location) => {
                          setOperation(prev => ({
                            ...prev,
                            lastKnownLocation: location
                          }));
                        }}
                      />
                      
                      {/* Ressources Assignées */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Truck className="w-4 h-4" />
                            Ressources Assignées
                            {operation.numberOfTrucks > 1 && (
                              <Badge variant="outline" className="ml-2">
                                {operation.assignments?.length || (operation.assignedDriver?.name ? 1 : 0)}/{operation.numberOfTrucks} camion(s) assigné(s)
                              </Badge>
                            )}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Opérateur et Field Ops */}
                          <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                            <div>
                              <p className="text-sm text-gray-500 mb-1">Opérateur Logistique</p>
                              <p className="font-medium">{operation.assignedOperatorName || 'Non assigné'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500 mb-1">Field Ops</p>
                              <p className="font-medium">{operation.assignedFieldOpsName || 'Non assigné'}</p>
                            </div>
                          </div>
                          
                          {/* Display ALL trucks (assigned and not assigned) */}
                          {operation.numberOfTrucks > 0 && (
                            <div className="space-y-3">
                              <p className="text-sm font-medium text-gray-700">
                                Camions et Chauffeurs ({operation.numberOfTrucks} demandé{operation.numberOfTrucks > 1 ? 's' : ''})
                              </p>
                              {Array.from({ length: operation.numberOfTrucks || 1 }, (_, index) => {
                                // Get assignment for this index (from new format or legacy)
                                const assignment = operation.assignments?.[index] || 
                                  (index === 0 && (operation.assignedDriver?.name || operation.assignedVehicle?.plate) ? {
                                    driverName: operation.assignedDriverName || operation.assignedDriver?.name,
                                    driverPhone: operation.assignedDriverPhone || operation.assignedDriver?.phone,
                                    vehiclePlate: operation.assignedVehiclePlate || operation.assignedVehicle?.plate,
                                    vehicleType: operation.assignedVehicle?.type || operation.vehicleType,
                                    vehicleBrand: operation.assignedVehicle?.brand,
                                    vehicleModel: operation.assignedVehicle?.model
                                  } : null);
                                
                                const isAssigned = assignment && (assignment.driverName || assignment.vehiclePlate);
                                
                                return (
                                  <div 
                                    key={index} 
                                    className={`rounded-lg p-3 border ${
                                      isAssigned 
                                        ? 'bg-green-50 border-green-200' 
                                        : 'bg-gray-50 border-gray-200 border-dashed'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <Badge 
                                          variant={isAssigned ? "default" : "outline"} 
                                          className={`text-xs ${isAssigned ? 'bg-green-600' : ''}`}
                                        >
                                          Camion {index + 1}
                                        </Badge>
                                        {assignment?.vehicleType && (
                                          <Badge variant="outline" className="text-xs">
                                            {getVehicleTypeLabel(assignment.vehicleType)}
                                          </Badge>
                                        )}
                                      </div>
                                      {isAssigned ? (
                                        <Badge variant="outline" className="text-xs text-green-600 border-green-600">
                                          ✓ Assigné
                                        </Badge>
                                      ) : (
                                        <Badge variant="outline" className="text-xs text-orange-500 border-orange-500">
                                          ⏳ En attente
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <p className="text-xs text-gray-500">Chauffeur</p>
                                        {isAssigned && assignment?.driverName ? (
                                          <>
                                            <p className="font-medium text-sm">{assignment.driverName}</p>
                                            {assignment.driverPhone && (
                                              <p className="text-xs text-gray-500">{assignment.driverPhone}</p>
                                            )}
                                          </>
                                        ) : (
                                          <p className="text-sm text-gray-400 italic">Non assigné</p>
                                        )}
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500">Véhicule</p>
                                        {isAssigned && assignment?.vehiclePlate ? (
                                          <>
                                            <p className="font-medium text-sm">{assignment.vehiclePlate}</p>
                                            {(assignment.vehicleBrand || assignment.vehicleModel) && (
                                              <p className="text-xs text-gray-500">
                                                {[assignment.vehicleBrand, assignment.vehicleModel].filter(Boolean).join(' ')}
                                              </p>
                                            )}
                                          </>
                                        ) : (
                                          <p className="text-sm text-gray-400 italic">Non assigné</p>
                                        )}
                                      </div>
                                    </div>
                                    {assignment?.hasTrailer && assignment?.trailerPlate && (
                                      <div className="mt-2 pt-2 border-t border-gray-200">
                                        <p className="text-xs text-gray-500">Remorque: <span className="font-medium">{assignment.trailerPlate}</span></p>
                                      </div>
                                    )}
                                    {assignment?.partnerName && (
                                      <div className="mt-2 pt-2 border-t border-gray-200">
                                        <p className="text-xs text-gray-500">Transporteur : <span className="font-medium text-amber-700">{assignment.partnerName}</span></p>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      
                      {/* Dates et Planification - Editable */}
                      <Card>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              Dates clés
                            </CardTitle>
                            {user?.role === 'admin' && !editingDates && (
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => {
                                  // Initialize dateEdits with current values
                                  const initEdits = {};
                                  [
                                    { field: 'deliveryDate', fallbacks: ['plannedArrivalDate', 'arrivalDate', 'desiredDeliveryDate'] },
                                    { field: 'departureDate', fallbacks: ['plannedDepartureDate', 'desiredPickupDate'] },
                                    { field: 'invoiceGeneratedAt' },
                                    { field: 'invoiceDepositDate' },
                                    { field: 'paymentReceivedAt' },
                                    { field: 'createdAt' },
                                  ].forEach(({ field, fallbacks }) => {
                                    const val = operation[field] || (fallbacks && fallbacks.reduce((v, f) => v || operation[f], null));
                                    initEdits[field] = toInputDate(val);
                                  });
                                  setEditingDates(true);
                                  setDateEdits(initEdits);
                                  setDateMotif('');
                                }}
                                className="text-xs text-blue-600 hover:text-blue-700"
                                data-testid="btn-edit-dates"
                              >
                                <Pencil className="w-3 h-3 mr-1" />
                                Modifier
                              </Button>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {/* Date fields */}
                          {[
                            { field: 'deliveryDate', label: 'Date opération (livraison)', fallbacks: ['plannedArrivalDate', 'arrivalDate', 'desiredDeliveryDate'] },
                            { field: 'departureDate', label: 'Date de départ', fallbacks: ['plannedDepartureDate', 'desiredPickupDate'] },
                            { field: 'invoiceGeneratedAt', label: 'Date de facturation' },
                            { field: 'invoiceDepositDate', label: 'Date dépôt facture' },
                            { field: 'paymentReceivedAt', label: 'Date paiement reçu' },
                            { field: 'createdAt', label: 'Date création (système)' },
                          ].map(({ field, label, fallbacks }) => {
                            const currentVal = operation[field] || (fallbacks && fallbacks.reduce((v, f) => v || operation[f], null));
                            return (
                              <div key={field} className="flex items-center justify-between gap-2">
                                <p className="text-xs text-gray-500 min-w-[130px]">{label}</p>
                                {editingDates ? (
                                  <input
                                    type="date"
                                    className="text-sm border rounded px-2 py-1 w-[150px]"
                                    value={dateEdits[field] || ''}
                                    onChange={(e) => setDateEdits(prev => ({...prev, [field]: e.target.value}))}
                                    data-testid={`date-input-${field}`}
                                  />
                                ) : (
                                  <p className="text-sm font-medium">
                                    {currentVal ? new Date(currentVal).toLocaleDateString('fr-FR') : <span className="text-gray-400 italic">Non défini</span>}
                                  </p>
                                )}
                              </div>
                            );
                          })}

                          {/* Save/Cancel for edit mode */}
                          {editingDates && (
                            <div className="pt-3 border-t space-y-2">
                              <textarea
                                placeholder="Motif de la correction (obligatoire, min 5 car.)"
                                className="w-full text-sm border rounded px-2 py-1.5 resize-none"
                                rows={2}
                                value={dateMotif}
                                onChange={(e) => setDateMotif(e.target.value)}
                                data-testid="date-edit-motif"
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                                  onClick={handleDateSave}
                                  disabled={savingDates || !dateMotif || dateMotif.trim().length < 5}
                                  data-testid="date-edit-save"
                                >
                                  {savingDates ? 'Enregistrement...' : 'Enregistrer'}
                                </Button>
                                <Button
                                  size="sm" variant="outline" className="flex-1"
                                  onClick={() => { setEditingDates(false); setDateEdits({}); setDateMotif(''); }}
                                  data-testid="date-edit-cancel"
                                >
                                  Annuler
                                </Button>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      
                      {/* Détails de la Marchandise */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Package className="w-4 h-4" />
                            Détails de la Marchandise
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-gray-500 mb-1">Type de marchandise</p>
                              <p className="font-medium capitalize">{formatMerchandiseType(operation.merchandiseType, operation.merchandiseTypeOther) || 'Non spécifié'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500 mb-1">Poids (tonnes)</p>
                              <p className="font-medium">{operation.weight ? `${operation.weight.toLocaleString('fr-FR')} T` : 'Non spécifié'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500 mb-1">Volume (m³)</p>
                              <p className="font-medium">{operation.volume ? `${operation.volume} m³` : 'Non spécifié'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500 mb-1">Valeur déclarée (XOF)</p>
                              <p className="font-medium">{operation.merchandiseValue ? `${operation.merchandiseValue.toLocaleString('fr-FR')} XOF` : 'Non spécifiée'}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>
                  
                  {/* Transferts Tab - Child Operations */}
                  <TabsContent value="transfers" className="mt-6">
                    <div className="space-y-4">
                      {/* Header with Add button */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold flex items-center gap-2">
                            <Users className="w-5 h-5 text-gray-500" />
                            Transferts liés
                          </h3>
                          <p className="text-sm text-gray-500">
                            Sous-opérations liées à cette opération principale
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={fetchChildOperations}
                            disabled={loadingChildren}
                          >
                            <RefreshCw className={`w-4 h-4 ${loadingChildren ? 'animate-spin' : ''}`} />
                          </Button>
                          {canAddChildren && (
                            <Button 
                              onClick={() => setShowAddChildModal(true)}
                              className="bg-red-600 hover:bg-red-700"
                              data-testid="add-child-btn"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Ajouter un transfert
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Summary Cards */}
                      {parentSummary && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <Card className="bg-gray-50">
                            <CardContent className="pt-4 pb-4">
                              <p className="text-2xl font-bold">{parentSummary.childrenSummary?.total || 0}</p>
                              <p className="text-xs text-gray-500">Total</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-blue-50">
                            <CardContent className="pt-4 pb-4">
                              <p className="text-2xl font-bold text-blue-700">{parentSummary.childrenSummary?.planned || 0}</p>
                              <p className="text-xs text-gray-500">Planifiés</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-amber-50">
                            <CardContent className="pt-4 pb-4">
                              <p className="text-2xl font-bold text-amber-700">{parentSummary.childrenSummary?.in_progress || 0}</p>
                              <p className="text-xs text-gray-500">En cours</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-emerald-50">
                            <CardContent className="pt-4 pb-4">
                              <p className="text-2xl font-bold text-emerald-700">{parentSummary.childrenSummary?.completed || 0}</p>
                              <p className="text-xs text-gray-500">Terminés</p>
                            </CardContent>
                          </Card>
                        </div>
                      )}

                      {/* Children List */}
                      {loadingChildren ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                      ) : childOperations.length === 0 ? (
                        <Card className="bg-gray-50">
                          <CardContent className="py-8 text-center">
                            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-gray-500 mb-4">Aucun transfert enfant pour cette opération</p>
                            {canAddChildren && (
                              <Button 
                                onClick={() => setShowAddChildModal(true)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Créer le premier transfert
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      ) : (
                        <div className="space-y-3">
                          {childOperations.map((child) => (
                            <Card key={child.id} className="hover:shadow-md transition-shadow">
                              <CardContent className="py-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <div>
                                      <p className="font-medium">{child.runNumber || child.id.slice(0, 8)}</p>
                                      <p className="text-sm text-gray-500">
                                        {formatChildDate(child.date)} • {child.shift === 'day' ? 'Jour' : 'Nuit'}
                                      </p>
                                    </div>
                                    {getChildStatusBadge(child.status)}
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div className="text-right text-sm">
                                      <p className="text-gray-600">
                                        {child.plannedVehiclePlate || child.inheritedData?.vehiclePlate || 'Véhicule non assigné'}
                                      </p>
                                      <p className="text-gray-500">
                                        {child.plannedDriverName || child.inheritedData?.driverName || 'Chauffeur non assigné'}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        onClick={() => navigate(`/admin/contracts/${child.contractId}/runs/${child.id}`)}
                                        title="Voir détails"
                                      >
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                      {canDeleteChildren && child.status !== 'in_progress' && child.status !== 'completed' && (
                                        <Button 
                                          size="sm" 
                                          variant="ghost"
                                          onClick={() => handleDeleteChild(child.id)}
                                          className="text-red-600 hover:text-red-700"
                                          title="Supprimer"
                                        >
                                          <XCircle className="w-4 h-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {/* Inherited data preview */}
                                {child.inheritedData && (
                                  <div className="mt-3 pt-3 border-t text-xs text-gray-500 flex flex-wrap gap-4">
                                    {child.inheritedData.departureAddress && (
                                      <span>Départ: {child.inheritedData.departureAddress}</span>
                                    )}
                                    {child.inheritedData.arrivalAddress && (
                                      <span>Arrivée: {child.inheritedData.arrivalAddress}</span>
                                    )}
                                    {child.counters && (
                                      <span>{child.counters.stopsTotal} étapes</span>
                                    )}
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="documents" className="mt-6">
                    <DocumentsComplianceCenter
                      operationId={operation.id}
                      operation={operation}
                      docData={docData}
                      onAddDoc={openAddDoc}
                      onRequestField={openRequestField}
                      hideSectionD
                      canValidate={hasPermission('operations.validate_documents')}
                    />
                  </TabsContent>

                  <TabsContent value="bons" className="mt-6">
                    <div className="space-y-6">
                      {/* PM Spec V3 13/05/2026 — Panneau unifié Bons & signatures */}
                      <LoadingNotesV3Panel
                        operation={operation}
                        currentUser={user}
                        entityKind="operation"
                      />
                      {/* Signature externe tablette - PM Spec 30/04/2026 */}
                      <ExternalSignCodeBlock operationId={operation.id} />
                      <OperationOCRDocuments operationId={operation.id} />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="incidents" className="mt-6">
                    <IncidentsPanel
                      operationId={operation.id || id}
                      operationReference={operation.reference}
                      token={'cookie-auth'}
                    />
                  </TabsContent>
                  
                  <TabsContent value="messages" className="mt-6">
                    <MessagingSystem
                      operationId={operation.id}
                      assignedOperator={
                        operation.assignedOperatorName ? {
                          id: operation.assignedOperatorId,
                          name: operation.assignedOperatorName
                        } : null
                      }
                      assignedFieldOps={
                        operation.assignedFieldOpsName ? {
                          id: operation.assignedFieldOpsId,
                          name: operation.assignedFieldOpsName
                        } : null
                      }
                      messages={[
                        {
                          id: '1',
                          operationId: operation.id,
                          senderId: operation.assignedOperatorId || 'admin',
                          senderName: operation.assignedOperatorName || 'Équipe Baobab',
                          senderType: 'admin',
                          content: 'Bonjour! Nous avons bien reçu votre demande de transport.',
                          timestamp: operation.createdAt,
                          read: true
                        }
                      ]}
                    />
                  </TabsContent>
                  <TabsContent value="notes" className="mt-6">
                    <InternalNotesTab
                      entityType="operation"
                      entityId={operation.id}
                      currentUser={user}
                      onCountChange={setInternalNotesCount}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar */}
          {activeTab === 'documents' ? (
            <DocumentsComplianceRail
              stats={docData.stats}
              operation={operation}
              onShowMissing={() => { window.dispatchEvent(new Event('op-docs-show-missing')); document.getElementById('op-docs-sections')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              onRequestField={() => openRequestField(docData.stats.manquantsList)}
              onImportMultiple={() => openAddDoc('autre')}
              onHistory={() => document.getElementById('op-docs-recent')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            />
          ) : (
          <SidebarRight
            operation={operation}
            onSendMessage={(recipient) => {
              // Store selected recipient in localStorage for MessagingSystem
              if (recipient) {
                localStorage.setItem('selected_message_recipient', JSON.stringify(recipient));
              }
              
              // Navigate to messages tab by clicking the TabsTrigger
              const messagesTab = document.querySelector('button[value="messages"]');
              if (messagesTab) {
                messagesTab.click();
                // Scroll to tabs section
                messagesTab.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }}
            ActionsComponent={
              <>
                {/* Admin Actions */}
                <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-amber-900 text-base">
                      <Shield className="w-4 h-4" />
                      Actions Administratives
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Responsables Section */}
                    <div className="bg-white rounded-lg p-3 space-y-3">
                      <h3 className="font-semibold text-gray-900 text-sm">Responsables</h3>
                      
                      {/* Operator */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 text-gray-700">
                            <User className="w-3 h-3" />
                            <span className="text-xs font-medium">Opérateur</span>
                          </div>
                          <Button
                            onClick={() => setShowOperatorModal(true)}
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            data-testid="assign-operator-btn"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Changer
                          </Button>
                        </div>
                        {operation.assignedOperatorName ? (
                          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold">
                              {operation.assignedOperatorName?.substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 text-xs">
                                {operation.assignedOperatorName}
                              </p>
                              <p className="text-xs text-emerald-700">
                                {operation.assignedOperatorJobTitle || 'Poste non renseigné'}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                            <p className="text-xs text-gray-500 italic">Non assigné</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Field Ops */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 text-gray-700">
                            <MapPin className="w-3 h-3" />
                            <span className="text-xs font-medium">Field Ops</span>
                          </div>
                          <Button
                            onClick={() => setShowFieldOpsModal(true)}
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            data-testid="assign-fieldops-btn"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            {operation.assignedFieldOpsName ? 'Changer' : 'Assigner'}
                          </Button>
                        </div>
                        {operation.assignedFieldOpsName ? (
                          <div className="bg-teal-50 border border-teal-200 rounded-lg p-2 flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center text-white text-xs font-bold">
                              {operation.assignedFieldOpsName?.substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 text-xs">
                                {operation.assignedFieldOpsName}
                              </p>
                              <p className="text-xs text-teal-700">
                                {operation.assignedFieldOpsJobTitle || 'Poste non renseigné'}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                            <p className="text-xs text-gray-500 italic">Non assigné</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Camions assignés - Résumé */}
                      {operation.numberOfTrucks > 0 && (
                        <div className="space-y-2 pt-2 border-t">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1 text-gray-700">
                              <Truck className="w-3 h-3" />
                              <span className="text-xs font-medium">
                                Camions ({operation.assignments?.length || (operation.assignedDriver?.name ? 1 : 0)}/{operation.numberOfTrucks})
                              </span>
                            </div>
                            <Button
                              onClick={() => setShowPlanificationModal(true)}
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Gérer
                            </Button>
                          </div>
                          <div className="space-y-1">
                            {Array.from({ length: operation.numberOfTrucks }, (_, index) => {
                              const assignment = operation.assignments?.[index] || 
                                (index === 0 && operation.assignedDriver?.name ? {
                                  driverName: operation.assignedDriverName || operation.assignedDriver?.name,
                                  vehiclePlate: operation.assignedVehiclePlate || operation.assignedVehicle?.plate
                                } : null);
                              const isAssigned = assignment && (assignment.driverName || assignment.vehiclePlate);
                              
                              return (
                                <div 
                                  key={index} 
                                  className={`rounded p-2 text-xs ${
                                    isAssigned 
                                      ? 'bg-green-50 border border-green-200' 
                                      : 'bg-orange-50 border border-orange-200 border-dashed'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">Camion {index + 1}</span>
                                    <span className={isAssigned ? 'text-green-600' : 'text-orange-500'}>
                                      {isAssigned ? '✓' : '⏳'}
                                    </span>
                                  </div>
                                  {isAssigned ? (
                                    <p className="text-gray-600 truncate">
                                      {assignment.driverName || 'Chauffeur ?'} • {assignment.vehiclePlate || 'Véhicule ?'}
                                    </p>
                                  ) : (
                                    <p className="text-orange-600 italic">En attente d'assignation</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions Rapides */}
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => setShowEditOperationModal(true)}
                        variant="outline"
                        size="sm"
                        className="h-auto py-3 flex flex-col items-center justify-center gap-1 bg-white hover:bg-blue-50 border-blue-200"
                        data-testid="edit-operation-btn"
                      >
                        <Pencil className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-medium text-blue-600">Modifier</span>
                      </Button>
                      <Button
                        onClick={() => setShowPlanificationModal(true)}
                        variant="outline"
                        size="sm"
                        className="h-auto py-3 flex flex-col items-center justify-center gap-1 bg-white hover:bg-gray-50"
                      >
                        <Activity className="w-4 h-4" />
                        <span className="text-xs font-medium">Planifier</span>
                      </Button>
                      <Button
                        onClick={() => setShowChargementModal(true)}
                        variant="outline"
                        size="sm"
                        className="h-auto py-3 flex flex-col items-center justify-center gap-1 bg-white hover:bg-gray-50"
                      >
                        <Package className="w-4 h-4" />
                        <span className="text-xs font-medium">Chargement</span>
                      </Button>
                      <Button
                        onClick={() => setShowLivraisonModal(true)}
                        variant="outline"
                        size="sm"
                        className="h-auto py-3 flex flex-col items-center justify-center gap-1 bg-white hover:bg-gray-50"
                      >
                        <Truck className="w-4 h-4" />
                        <span className="text-xs font-medium">Livraison</span>
                      </Button>
                      <Button
                        onClick={() => setShowFacturationModal(true)}
                        variant="outline"
                        size="sm"
                        className="h-auto py-3 flex flex-col items-center justify-center gap-1 bg-white hover:bg-gray-50"
                      >
                        <DollarSign className="w-4 h-4" />
                        <span className="text-xs font-medium">Facture</span>
                      </Button>
                      <Button
                        onClick={() => setShowAdvancesModal(true)}
                        variant="outline"
                        size="sm"
                        className="h-auto py-3 flex flex-col items-center justify-center gap-1 bg-amber-50 hover:bg-amber-100 border-amber-300 text-amber-800"
                        data-testid="advances-open-btn"
                      >
                        <DollarSign className="w-4 h-4" />
                        <span className="text-xs font-medium">Acomptes</span>
                        {(operation.advances || []).length > 0 && (
                          <span className="text-[10px] bg-amber-600 text-white rounded-full px-1.5 py-0.5">
                            {operation.advances.length}
                          </span>
                        )}
                      </Button>
                    </div>

                    {/* Action Principale basée sur le statut */}
                    {operation.status === 'demande_enregistree' && (
                      <Button 
                        onClick={() => handleStatusChange('en_attente_de_devis', false)}
                        disabled={updatingStatus}
                        className="w-full h-auto py-3 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        {updatingStatus ? 'Mise à jour...' : 'Passer en attente devis'}
                      </Button>
                    )}
                    
                    {operation.status === 'en_attente_de_devis' && (
                      <Button 
                        onClick={() => setShowCreateQuoteModal(true)}
                        className="w-full h-auto py-3 text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Créer un Devis
                      </Button>
                    )}

                    {/* Bouton Réviser le Devis - pour négociation ou modification */}
                    {['en_attente_validation_client', 'en_negociation', 'devis_refuse'].includes(operation.status) && (
                      <div className="space-y-2">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-amber-700 mb-2">
                            <RefreshCw className="w-4 h-4" />
                            <span className="font-semibold text-sm">
                              {operation.status === 'en_negociation' ? 'Négociation en cours' : 
                               operation.status === 'devis_refuse' ? 'Devis refusé' : 'En attente de validation'}
                            </span>
                          </div>
                          <p className="text-xs text-amber-600 mb-3">
                            Vous pouvez réviser le devis et le renvoyer au client avec un nouveau montant.
                          </p>
                          <Button 
                            onClick={() => setShowReviseQuoteModal(true)}
                            className="w-full h-auto py-3 text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Réviser le Devis
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Bouton Générer Facture quand devis accepté */}
                    {(operation.status === 'devis_accepte' || operation.status === 'planifiee') && !operation.invoiceNumber && (
                      <div className="space-y-2">
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-emerald-700 mb-2">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="font-semibold text-sm">Devis accepté</span>
                          </div>
                          <p className="text-xs text-emerald-600 mb-3">
                            Le client a accepté le devis. Vous pouvez maintenant générer la facture.
                          </p>
                          <Button 
                            onClick={() => {
                              setInvoiceGenMode('invoice-from-quote');
                              setShowInvoiceGenerationModal(true);
                            }}
                            className="w-full h-auto py-3 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="generate-invoice-from-quote-btn"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Générer la Facture
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Info facture déjà générée — TOUJOURS VISIBLE */}
                    {operation.invoiceNumber && (
                      <CollapsibleCard
                        id="op-invoice-billing"
                        title={`Facture ${operation.invoiceNumber}`}
                        icon={FileText}
                        defaultOpen={true}
                        className="border-blue-200 bg-blue-50/40"
                        headerClassName="text-blue-900"
                        headerBadge={operation.fneCertification?.status === 'certified' ? (
                          <Badge className="bg-emerald-100 text-emerald-800 text-[10px] h-4 ml-1 border-0">🔒 FNE</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] h-4 ml-1 border-amber-400 text-amber-700">FNE</Badge>
                        )}
                        summaryText={operation.fneCertification?.reference ? `Réf. DGI ${operation.fneCertification.reference}` : 'Non certifiée'}
                      >
                      <div className="space-y-3">
                        <div className="flex items-center justify-end gap-1">
                            <Button
                              onClick={() => setShowRegenerateInvoiceModal(true)}
                              variant="outline"
                              size="sm"
                              className="text-amber-600 border-amber-300 hover:bg-amber-100"
                              title="Régénérer la facture avec un nouveau numéro"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Régénérer
                            </Button>
                            <Button
                              onClick={() => generatePDF('invoice')}
                              variant="outline"
                              size="sm"
                              className="text-blue-600 border-blue-300 hover:bg-blue-100"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                        </div>

                        {/* Bouton Enregistrer Paiement — TOUJOURS accessible */}
                        <div className="space-y-2">
                          <Button
                            onClick={() => setShowPaymentModal(true)}
                            className="w-full h-auto py-2 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="record-payment-btn"
                          >
                            <DollarSign className="w-4 h-4 mr-2" />
                            Enregistrer un paiement
                          </Button>

                          {/* Bouton Envoyer lien Wave */}
                          <Button
                            onClick={() => setShowWavePaymentModal(true)}
                            variant="outline"
                            className="w-full h-auto py-2 text-sm font-semibold border-purple-300 text-purple-700 hover:bg-purple-50"
                            data-testid="open-wave-link-modal-btn"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Lien de paiement Wave
                          </Button>
                        </div>

                        {/* FNE — Bloc dédié certification DGI (sous Enregistrer paiement) */}
                        <FneBillingCard
                          certification={operation.fneCertification}
                          fneExemption={operation.fneExemption}
                          onCertify={() => setShowFneCertifyModal(true)}
                          onRetry={() => setShowFneCertifyModal(true)}
                          onViewPdf={() => {
                            const url = `${process.env.REACT_APP_BACKEND_URL}/api/fne/pdf/spot/${operation.id}`;
                            window.open(url, '_blank', 'noopener');
                          }}
                          onExempt={() => { setFneExemptMode('exempt'); setShowFneExemptionDialog(true); }}
                          onReinclude={() => { setFneExemptMode('reinclude'); setShowFneExemptionDialog(true); }}
                          onCreateCreditNote={() => setShowCreditNoteDialog(true)}
                          onViewCreditNote={creditNotes[0]?.credit_note_number ? () => {
                            const url = `${process.env.REACT_APP_BACKEND_URL}/api/fne/credit-notes/${creditNotes[0].credit_note_number}/pdf`;
                            window.open(url, '_blank', 'noopener');
                          } : undefined}
                          creditNote={creditNotes[0]}
                          canRefund={canRefundFne}
                          certifyDisabled={!operation.compteContribuable}
                          disabledReason={!operation.compteContribuable ? 'NCC client manquant — requis pour FNE' : undefined}
                          canExempt={user?.role === 'admin'}
                        />
                        <FneCreditNoteDialog
                          open={showCreditNoteDialog}
                          onOpenChange={setShowCreditNoteDialog}
                          operationId={operation.id}
                          invoiceLabel={operation.invoiceNumber || operation.operationNumber}
                          certification={operation.fneCertification}
                          onSuccess={() => { fetchOperation(); fetchCreditNotes(); }}
                        />
                        <FneCertifyModal
                          open={showFneCertifyModal}
                          onClose={() => setShowFneCertifyModal(false)}
                          onSuccess={() => { setShowFneCertifyModal(false); fetchOperation(); }}
                          targetType="spot"
                          targetId={operation.id}
                          targetLabel={operation.invoiceNumber || operation.operationNumber}
                        />
                        <FneExemptionDialog
                          open={showFneExemptionDialog}
                          onOpenChange={setShowFneExemptionDialog}
                          operationId={operation.id}
                          operationLabel={operation.invoiceNumber || operation.operationNumber}
                          mode={fneExemptMode}
                          onSuccess={() => { setShowFneExemptionDialog(false); fetchOperation(); }}
                        />
                        
                        {/* Badge paiement complet */}
                        {operation.financialStatus === 'paid' && (
                          <div className="flex items-center justify-between gap-2 text-green-700 bg-green-100 rounded px-2 py-1">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4" />
                              <span className="text-sm font-medium">Paiement complet reçu</span>
                            </div>
                            {['admin', 'lead_commercial', 'commercial'].includes(user?.role?.toLowerCase()) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50 h-7 px-2"
                                onClick={() => setShowReversePaymentModal(true)}
                                title="Annuler ce paiement (correction)"
                                data-testid="reverse-payment-btn"
                              >
                                <RefreshCw className="w-3.5 h-3.5 mr-1" />
                                Annuler
                              </Button>
                            )}
                          </div>
                        )}
                        
                        {/* Badge paiement partiel */}
                        {operation.financialStatus === 'partially_paid' && (
                          <div className="flex items-center justify-between gap-2 text-amber-700 bg-amber-100 rounded px-2 py-1">
                            <span className="text-sm">Paiement partiel</span>
                            <span className="text-sm font-medium">
                              {((operation.amountPaid || 0)).toLocaleString('fr-FR')} / {((operation.invoiceAmount || 0)).toLocaleString('fr-FR')} XOF
                            </span>
                            {['admin', 'lead_commercial', 'commercial'].includes(user?.role?.toLowerCase()) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50 h-7 px-2"
                                onClick={() => setShowReversePaymentModal(true)}
                                title="Annuler le paiement"
                                data-testid="reverse-partial-payment-btn"
                              >
                                <RefreshCw className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                      </CollapsibleCard>
                    )}

                    {/* Changement de statut - Boutons avec icônes */}
                    {allowedStatuses.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                          Changer le statut
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {allowedStatuses.map((status) => {
                            // Définir l'icône et la couleur pour chaque statut
                            const getStatusConfig = (value) => {
                              const configs = {
                                'prete_au_chargement': {
                                  icon: <CheckCircle2 className="w-4 h-4" />,
                                  color: 'text-emerald-600',
                                  bgHover: 'hover:bg-emerald-50',
                                  label: 'Prêt au chargement'
                                },
                                'en_cours_de_chargement': {
                                  icon: <Package className="w-4 h-4" />,
                                  color: 'text-blue-600',
                                  bgHover: 'hover:bg-blue-50',
                                  label: 'En chargement'
                                },
                                'charge_en_route': {
                                  icon: <Truck className="w-4 h-4" />,
                                  color: 'text-cyan-600',
                                  bgHover: 'hover:bg-cyan-50',
                                  label: 'En route'
                                },
                                'livree': {
                                  icon: <CheckCircle2 className="w-4 h-4" />,
                                  color: 'text-teal-600',
                                  bgHover: 'hover:bg-teal-50',
                                  label: 'Livrée'
                                },
                                'en_attente_paiement': {
                                  icon: <DollarSign className="w-4 h-4" />,
                                  color: 'text-amber-600',
                                  bgHover: 'hover:bg-amber-50',
                                  label: 'En attente paiement'
                                },
                                'payee': {
                                  icon: <CheckCircle2 className="w-4 h-4" />,
                                  color: 'text-green-600',
                                  bgHover: 'hover:bg-green-50',
                                  label: 'Payée'
                                },
                                'terminee': {
                                  icon: <CheckCircle2 className="w-4 h-4" />,
                                  color: 'text-gray-600',
                                  bgHover: 'hover:bg-gray-50',
                                  label: 'Terminée'
                                },
                                'annulee_admin': {
                                  icon: <AlertCircle className="w-4 h-4" />,
                                  color: 'text-red-600',
                                  bgHover: 'hover:bg-red-50',
                                  label: 'Annulée'
                                },
                                'en_negociation': {
                                  icon: <MessageSquare className="w-4 h-4" />,
                                  color: 'text-purple-600',
                                  bgHover: 'hover:bg-purple-50',
                                  label: 'En négociation'
                                },
                                'planifiee': {
                                  icon: <Activity className="w-4 h-4" />,
                                  color: 'text-indigo-600',
                                  bgHover: 'hover:bg-indigo-50',
                                  label: 'Planifiée'
                                },
                                'devis_accepte': {
                                  icon: <CheckCircle2 className="w-4 h-4" />,
                                  color: 'text-emerald-600',
                                  bgHover: 'hover:bg-emerald-50',
                                  label: 'Devis accepté'
                                }
                              };
                              return configs[value] || {
                                icon: <FileText className="w-4 h-4" />,
                                color: 'text-gray-600',
                                bgHover: 'hover:bg-gray-50',
                                label: status.label
                              };
                            };

                            const config = getStatusConfig(status.value);

                            return (
                              <Button
                                key={status.value}
                                onClick={() => handleStatusChange(status.value)}
                                disabled={updatingStatus}
                                variant="outline"
                                size="sm"
                                className={`h-auto py-3 flex flex-col items-center justify-center gap-1 bg-white ${config.bgHover} ${config.color} border-gray-200`}
                              >
                                {config.icon}
                                <span className="text-xs font-medium text-center leading-tight">
                                  {config.label}
                                </span>
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Actions Secondaires */}
                    <div className="space-y-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full h-auto py-2 bg-white hover:bg-gray-50 text-xs"
                        onClick={() => setShowHistoriqueModal(true)}
                      >
                        <History className="w-3 h-3 mr-1" />
                        Historique complet
                      </Button>
                      
                      <Button
                        variant="destructive"
                        size="sm"
                        className="w-full h-auto py-2 bg-red-600 hover:bg-red-700 text-white text-xs"
                        onClick={() => {
                          setShowCancelModal(true);
                          setCancelReason('');
                        }}
                        data-testid="cancel-operation-btn"
                      >
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Annuler l&apos;opération
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Facturation (Feature Cohérence Statut × Facturation) */}
                <Card data-testid="billing-status-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <DollarSign className="w-4 h-4 text-[#1db584]" />
                      Facturation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Statut financier canonique */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Statut financier</span>
                      <FinancialStatusBadge status={operation.financialStatus || 'pending'} />
                    </div>

                    {/* Lien facture(s) générée(s) — par défaut, seule la facture courante (la plus récente) */}
                    {operationInvoices.length > 0 && (() => {
                      const sorted = [...operationInvoices].sort((a, b) => {
                        const da = new Date(a.generated_at || a.created_at || 0).getTime();
                        const dbt = new Date(b.generated_at || b.created_at || 0).getTime();
                        return dbt - da;
                      });
                      const currentInv = sorted.find((d) => d.document_number === operation.invoiceNumber) || sorted[0];
                      const previousInvs = sorted.filter((d) => d.document_number !== currentInv.document_number);
                      const renderInv = (inv, isCurrent) => (
                        <a
                          key={inv.document_number || inv.id}
                          href={inv.file_path ? `${API}/documents/download/${inv.document_number}` : '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center justify-between gap-2 text-sm hover:underline ${isCurrent ? 'text-blue-700 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                          data-testid={`invoice-link-${inv.document_number}`}
                        >
                          <span className="flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5" />
                            {inv.document_number || 'Facture'}
                            {isCurrent && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1 border-emerald-300 text-emerald-700">Active</Badge>
                            )}
                          </span>
                          {inv.generated_at && (
                            <span className="text-xs text-gray-400">
                              {new Date(inv.generated_at).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </a>
                      );
                      return (
                        <div className="pt-2 border-t space-y-1.5">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Facture en cours</p>
                          {renderInv(currentInv, true)}
                          {previousInvs.length > 0 && (
                            <>
                              <button
                                type="button"
                                onClick={() => setShowInvoicesHistory((v) => !v)}
                                className="text-[10px] text-gray-500 hover:text-gray-700 underline mt-1"
                                data-testid="toggle-invoices-history"
                              >
                                {showInvoicesHistory
                                  ? `Masquer l'historique (${previousInvs.length})`
                                  : `Voir l'historique des régénérations (${previousInvs.length})`}
                              </button>
                              {showInvoicesHistory && (
                                <div className="mt-1 pl-2 border-l-2 border-gray-200 space-y-1">
                                  <p className="text-[10px] text-gray-400 italic">Versions précédentes archivées (audit OHADA)</p>
                                  {previousInvs.map((inv) => renderInv(inv, false))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })()}

                    {/* Message spécial pour opérations contrat */}
                    {operation.linkedContractId && (
                      <div className="pt-2 border-t text-xs text-gray-500 italic flex items-start gap-1.5">
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>
                          Opération contractuelle — facturée mensuellement avec les autres runs du contrat.
                        </span>
                      </div>
                    )}

                    {/* Message si aucune facture générée et statut quote_accepted */}
                    {operationInvoices.length === 0
                      && !operation.linkedContractId
                      && ['quote_accepted', 'invoiced'].includes(operation.financialStatus)
                      && operation.status === 'livree' && (
                      <div className="pt-2 border-t text-xs text-amber-700 bg-amber-50 rounded p-2 italic">
                        Livraison terminée. Passer le statut en "En attente de paiement" pour générer automatiquement la facture.
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Client Information */}
                <CollapsibleCard
                  id="op-client-info"
                  title="Informations Client"
                  icon={User}
                  defaultOpen={false}
                  headerClassName="text-[#1db584]"
                  summaryText={operation.companyName || `${operation.firstName || ''} ${operation.lastName || ''}`.trim()}
                  headerBadge={
                    <span className="flex items-center gap-1 ml-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setReassignClientOpen(true); }}
                        data-testid="reassign-client-btn"
                        className="h-6 text-[10px] py-0 px-2"
                      >
                        Changer de client
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setEditClientOpen(true); }}
                        data-testid="edit-client-info-btn"
                        className="h-6 text-[10px] py-0 px-2"
                      >
                        Modifier
                      </Button>
                    </span>
                  }
                >
                  <div className="space-y-2" data-testid="client-info-card">
                    <ExternalRefField
                      operation={operation}
                      isStaff
                      onUpdated={fetchOperation}
                      className="pb-1"
                    />
                    <div>
                      <p className="text-xs text-gray-500">Nom complet</p>
                      <p className="font-medium text-sm">{operation.firstName} {operation.lastName}</p>
                    </div>
                    <Separator />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Mail className="w-3 h-3 text-gray-400" />
                        <a href={`mailto:${operation.email}`} className="text-[#1db584] hover:underline text-xs">
                          {operation.email}
                        </a>
                      </div>
                    </div>
                    <Separator />
                    <div>
                      <p className="text-xs text-gray-500">Téléphone</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Phone className="w-3 h-3 text-gray-400" />
                        <a href={`tel:${operation.phone}`} className="text-[#1db584] hover:underline text-xs">
                          {operation.phone}
                        </a>
                      </div>
                    </div>
                    <Separator />
                    <div>
                      <p className="text-xs text-gray-500">Type de compte</p>
                      <Badge variant="secondary" className="mt-1 capitalize text-xs">
                        {operation.accountType}
                      </Badge>
                    </div>
                    {operation.accountType === 'entreprise' && (
                      <>
                        <Separator />
                        <div>
                          <p className="text-xs text-gray-500">Entreprise</p>
                          <p className="font-medium text-sm">{operation.companyName || <span className="text-gray-400 italic">—</span>}</p>
                        </div>
                        <Separator />
                        <div data-testid="client-rccm">
                          <p className="text-xs text-gray-500">RCCM</p>
                          <p className="font-medium text-sm">{operation.rccm || <span className="text-gray-400 italic">—</span>}</p>
                        </div>
                        <Separator />
                        <div data-testid="client-ncc">
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            NCC (Compte Contribuable)
                            {!operation.compteContribuable && (
                              <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 bg-amber-50 ml-1">
                                Requis pour FNE
                              </Badge>
                            )}
                          </p>
                          <p className="font-medium text-sm font-mono">
                            {operation.compteContribuable || <span className="text-amber-600 italic font-sans">⚠ Manquant — cliquez Modifier</span>}
                          </p>
                        </div>
                        <Separator />
                        <div data-testid="client-tva">
                          <p className="text-xs text-gray-500">Statut TVA</p>
                          <p className="font-medium text-sm capitalize">
                            {(operation.tvaStatus || 'assujetti').replace('_', ' ')}
                          </p>
                        </div>
                        <Separator />
                        <div data-testid="client-address">
                          <p className="text-xs text-gray-500">Adresse</p>
                          <p className="font-medium text-sm">{operation.address || <span className="text-gray-400 italic">—</span>}</p>
                        </div>
                        <Separator />
                        <div data-testid="client-billing-email">
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            Email facturation (FNE)
                            {!operation.billingEmail && (
                              <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 bg-amber-50 ml-1">
                                Fallback email principal
                              </Badge>
                            )}
                          </p>
                          <p className="font-medium text-sm">
                            {operation.billingEmail || <span className="text-gray-400 italic">{operation.email || '—'}</span>}
                          </p>
                        </div>
                        <Separator />
                        <div data-testid="client-sector">
                          <p className="text-xs text-gray-500">Secteur d&apos;activité</p>
                          <p className="font-medium text-sm">
                            {operation.sector_name || operation.custom_sector || <span className="text-gray-400 italic">—</span>}
                          </p>
                        </div>
                      </>
                    )}
                    {/* Module 1+2 — Statut Facturation FNE + Code TVA effectif */}
                    {operation.clientId && (
                      <FneBillingClientCard
                        client={{
                          id: operation.clientId,
                          tvaStatus: operation.tvaStatus,
                          compteContribuable: operation.compteContribuable,
                          companyName: operation.companyName,
                          fneBillingEnabled: operation.clientFneBillingEnabled,
                          fneBillingStartDate: operation.clientFneBillingStartDate,
                        }}
                        currentUser={user}
                        onToggled={fetchOperation}
                      />
                    )}
                  </div>
                </CollapsibleCard>

                {/* Modal édition infos client */}
                <ClientInfoEditDialog
                  open={editClientOpen}
                  onClose={() => setEditClientOpen(false)}
                  operation={operation}
                  onSaved={() => { setEditClientOpen(false); fetchOperation(); }}
                />

                {/* Modal réaffectation du client de l'opération */}
                <ReassignClientDialog
                  open={reassignClientOpen}
                  onClose={() => setReassignClientOpen(false)}
                  operation={operation}
                  onSaved={() => { setReassignClientOpen(false); fetchOperation(); }}
                />

                {/* Bandeau de marge (Lot A) — statut, palier, dérogation */}
                {canSeeSupplierFinance && (
                  <div className="mb-4">
                    <OperationMarginBanner
                      operationId={operation.id}
                      canOverride={['admin', 'super_admin'].includes((user?.role || '').toLowerCase())}
                      onChanged={fetchOperation}
                      refreshToken={`${operation.carrierPrice ?? ''}|${operation.montantTotalHT ?? ''}|${operation.finalPrice ?? ''}|${operation.updatedAt ?? ''}`}
                    />
                  </div>
                )}

                {/* Admin Financial Section - Finance fournisseur (marge) — masquée pour le pôle Ops */}
                {canSeeSupplierFinance && (
                <AdminFinancialSection 
                  operation={operation}
                  onRefresh={fetchOperation}
                  onEditTariffViaQuote={() => {
                    // PM Spec 28/04/2026 (Q2=a) — Bouton "Modifier via le devis" depuis la card financière.
                    // Choix du modal selon le statut : révision (versioning) si devis déjà émis,
                    // sinon édition normale (CreateDetailedQuoteModal détecte les quoteItems existants).
                    const reviseAllowed = [
                      'en_attente_de_devis',
                      'devis_cree',
                      'en_attente_validation_client',
                      'en_negociation',
                      'devis_refuse',
                    ].includes(operation?.status);
                    if (reviseAllowed) {
                      setShowReviseQuoteModal(true);
                    } else {
                      setShowCreateQuoteModal(true);
                    }
                  }}
                />
                )}
                
                {/* Financial Correction Actions - for payee/terminee status */}
                {operation.status === 'payee' && user?.role === 'admin' && (
                  <Card className="border-amber-200 bg-amber-50/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base text-amber-700">
                        <History className="w-4 h-4" />
                        Corrections financières
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button 
                        variant="outline" size="sm" className="w-full justify-start text-amber-700 border-amber-300 hover:bg-amber-100"
                        onClick={() => { setRetroTarget('en_attente_paiement'); setShowRetroStatusDialog(true); }}
                        data-testid="btn-retro-attente-paiement"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Revenir à "En attente paiement"
                      </Button>
                      <Button 
                        variant="outline" size="sm" className="w-full justify-start text-amber-700 border-amber-300 hover:bg-amber-100"
                        onClick={() => { setRetroTarget('livree'); setShowRetroStatusDialog(true); }}
                        data-testid="btn-retro-livree"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Revenir à "Livrée"
                      </Button>
                      <Separator className="my-2" />
                      <Button 
                        variant="outline" size="sm" className="w-full justify-start"
                        onClick={() => setShowFinancialEditDialog(true)}
                        data-testid="btn-financial-edit"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Modifier montants / méthode
                      </Button>
                      {financialCorrections.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-amber-200">
                          <p className="text-xs font-medium text-amber-700 mb-2">
                            Historique ({financialCorrections.length} correction{financialCorrections.length > 1 ? 's' : ''})
                          </p>
                          {financialCorrections.slice(0, 3).map((c, i) => (
                            <div key={i} className="text-xs text-gray-600 mb-1.5 p-1.5 bg-white rounded border">
                              <span className="font-medium">{c.type === 'retro_status' ? 'Rétro-statut' : 'Modification financière'}</span>
                              <span className="text-gray-400 ml-1">— {c.auteur_nom}</span>
                              <p className="text-gray-500 mt-0.5 italic">"{c.motif}"</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* WhatsApp Notifications */}
                <WhatsAppNotificationPanel 
                  operation={operation} 
                  onRefresh={fetchOperation}
                />

                {/* Pricing — visible uniquement si un prix est disponible (rôles avec finance) */}
                {operation.estimatedPrice != null && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <DollarSign className="w-4 h-4 text-[#1db584]" />
                      Tarification
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div>
                      <p className="text-xs text-gray-500">Prix estimé initial</p>
                      <p className="text-xl font-bold text-gray-700 mt-1">
                        {operation.estimatedPrice?.toLocaleString('fr-FR')} XOF
                      </p>
                    </div>
                    {operation.finalPrice && operation.finalPrice !== operation.estimatedPrice && (
                      <>
                        <Separator />
                        <div>
                          <p className="text-xs text-gray-500">Prix final négocié</p>
                          <p className="text-2xl font-bold text-[#1db584] mt-1">
                            {operation.finalPrice?.toLocaleString('fr-FR')} XOF
                          </p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
                )}
              </>
            }
          />
          )}
        </div>
      </div>

      {/* Modales documents (centre de conformité) */}
      {operation && (
        <>
          <AddDocumentModal
            open={addDocState.open}
            onOpenChange={(v) => setAddDocState((s) => ({ ...s, open: v }))}
            operationId={operation.id}
            defaultType={addDocState.type}
            initialFiles={addDocState.files}
            onUploaded={docData.refetch}
          />
          <RequestFieldModal
            open={reqFieldState.open}
            onOpenChange={(v) => setReqFieldState((s) => ({ ...s, open: v }))}
            operationId={operation.id}
            missingLabels={reqFieldState.labels}
          />
        </>
      )}

      {/* Modals */}
      {operation && (
        <>
          {/* Operator Assignment Modal */}
          <OperatorAssignmentModal
            open={showOperatorModal}
            onClose={() => setShowOperatorModal(false)}
            operationId={operation.id}
            currentOperatorId={operation.assignedOperatorId}
            onSuccess={() => {
              fetchOperation();
              setShowOperatorModal(false);
            }}
          />

          {/* Field Ops Assignment Modal */}
          <FieldOpsAssignmentModal
            open={showFieldOpsModal}
            onClose={() => setShowFieldOpsModal(false)}
            operationId={operation.id}
            currentFieldOpsId={operation.assignedFieldOpsId}
            onSuccess={() => {
              fetchOperation();
              setShowFieldOpsModal(false);
            }}
          />

          <PlanificationModal
            open={showPlanificationModal}
            onClose={() => setShowPlanificationModal(false)}
            operation={operation}
            onSuccess={handleModalSuccess}
          />
          
          <ChargementModal
            open={showChargementModal}
            onClose={() => setShowChargementModal(false)}
            operation={operation}
            onSuccess={handleModalSuccess}
          />
          
          <EnRouteModal
            open={showEnRouteModal}
            onClose={() => setShowEnRouteModal(false)}
            operation={operation}
            onSuccess={handleModalSuccess}
          />
          
          <LivraisonModal
            open={showLivraisonModal}
            onClose={() => setShowLivraisonModal(false)}
            operation={operation}
            onSuccess={handleModalSuccess}
          />
          
          <FacturationModal
            open={showFacturationModal}
            onClose={() => setShowFacturationModal(false)}
            operation={operation}
            onSuccess={handleModalSuccess}
          />

          {/* Payment Modal */}
          <PaymentModal
            open={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            operation={operation}
            onSuccess={handleModalSuccess}
          />

          {/* Payment Reversal Modal (PM Spec 03/06/2026) */}
          <PaymentReversalModal
            open={showReversePaymentModal}
            onClose={() => setShowReversePaymentModal(false)}
            onSuccess={() => fetchOperation()}
            type="spot"
            resourceId={operation.id}
            resource={{
              invoiceNumber: operation.invoiceNumber || operation.operationNumber,
              clientName: operation.companyName,
              amountPaid: operation.amountPaid,
              paidAt: operation.paymentReceivedAt,
            }}
          />

          {/* Wave Payment Link Modal */}
          <WavePaymentLinkModal
            open={showWavePaymentModal}
            onClose={() => setShowWavePaymentModal(false)}
            operation={operation}
            onSuccess={() => {
              fetchOperation();
            }}
          />

          {/* Loading / Unloading Note Modal — Wizard 5 étapes aligné portail public (PM Spec 06/05/2026) */}
          <LoadingNoteWizardModal
            open={loadingNoteModal.open}
            onOpenChange={(o) => {
              setLoadingNoteModal(s => ({ ...s, open: o }));
              if (!o) fetchLoadingNotesStatus(operation.id);
            }}
            operation={operation}
            type={loadingNoteModal.type}
            entityKind="operation"
            onSuccess={() => fetchLoadingNotesStatus(operation.id)}
          />

          {/* Create Detailed Quote Modal */}
          <CreateDetailedQuoteModal
            open={showCreateQuoteModal}
            onClose={() => setShowCreateQuoteModal(false)}
            operation={operation}
            onSuccess={() => {
              fetchOperation();
              setShowCreateQuoteModal(false);
            }}
          />

          {/* Revise Quote Modal - Uses same modal with revision mode */}
          <CreateDetailedQuoteModal
            open={showReviseQuoteModal}
            onClose={() => setShowReviseQuoteModal(false)}
            operation={operation}
            onSuccess={() => {
              fetchOperation();
              setShowReviseQuoteModal(false);
            }}
            isRevision={true}
          />

          {/* Historique Modal */}
          <HistoriqueModal
            open={showHistoriqueModal}
            onClose={() => setShowHistoriqueModal(false)}
            operationId={id}
            operation={operation}
          />

          {/* Modal "Transition inhabituelle" — Q3b soft-block */}
          {unusualTransitionModal && (
            <Dialog open={true} onOpenChange={() => setUnusualTransitionModal(null)}>
              <DialogContent className="max-w-lg" data-testid="unusual-transition-modal">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-amber-700">
                    <AlertCircle className="w-5 h-5" />
                    Confirmation de clôture sans paiement
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                    {unusualTransitionModal.warning}
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Statut actuel :</span>
                      <span className="font-medium">{getStatusLabel(unusualTransitionModal.currentStatus)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Nouveau statut :</span>
                      <span className="font-medium">{getStatusLabel(unusualTransitionModal.newStatus)}</span>
                    </div>
                    {unusualTransitionModal.financialStatus && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Statut financier :</span>
                        <span className="font-medium">{unusualTransitionModal.financialStatus}</span>
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter className="gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setUnusualTransitionModal(null)}
                    data-testid="cancel-unusual-transition"
                  >
                    Annuler
                  </Button>
                  <Button
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => {
                      const { newStatus, reason } = unusualTransitionModal;
                      setUnusualTransitionModal(null);
                      handleStatusChange(newStatus, false, reason, true); // confirmUnusual=true
                    }}
                    data-testid="confirm-unusual-transition"
                  >
                    Confirmer la clôture sans paiement
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* PM Spec 17/02/2026 — Advances Manager Modal */}
          <AdvancesManager
            open={showAdvancesModal}
            onOpenChange={setShowAdvancesModal}
            operation={operation}
            onAdvancesChange={fetchOperation}
          />

          {/* Regenerate Invoice Modal */}
          <RegenerateInvoiceModal
            open={showRegenerateInvoiceModal}
            onClose={() => setShowRegenerateInvoiceModal(false)}
            operation={operation}
            onSuccess={() => {
              fetchOperation();
              setShowRegenerateInvoiceModal(false);
            }}
          />

          {/* Invoice Generation Modal with Payment Terms */}
          <InvoiceGenerationModal
            open={showInvoiceGenerationModal}
            onClose={() => setShowInvoiceGenerationModal(false)}
            operation={operation}
            mode={invoiceGenMode}
            onSuccess={fetchOperation}
          />

          {/* Edit Operation Modal */}
          <EditOperationModal
            isOpen={showEditOperationModal}
            onClose={() => setShowEditOperationModal(false)}
            operation={operation}
            userRole={user?.role}
            onSuccess={() => {
              fetchOperation();
              setShowEditOperationModal(false);
            }}
          />

          {/* Add Child Transfer Modal */}
          <Dialog open={showAddChildModal} onOpenChange={setShowAddChildModal}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Nouveau Transfert</DialogTitle>
                <DialogDescription>
                  Créer un transfert enfant pour cette opération
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Inherited Data Display */}
                <Card className="bg-gray-50">
                  <CardContent className="py-3">
                    <p className="text-xs text-gray-500 mb-2">Données héritées de l'opération principale</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Client:</span>
                        <span className="ml-1 font-medium">
                          {operation?.firstName} {operation?.lastName}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Départ:</span>
                        <span className="ml-1 font-medium truncate" title={operation?.departure}>
                          {operation?.departure?.slice(0, 25)}...
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Véhicule:</span>
                        <span className="ml-1 font-medium">
                          {operation?.assignedVehicle?.plate || 'Non assigné'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Chauffeur:</span>
                        <span className="ml-1 font-medium">
                          {operation?.assignedDriver?.name || 'Non assigné'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Date & Shift */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={childForm.date}
                      onChange={(e) => setChildForm({ ...childForm, date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Shift</Label>
                    <Select 
                      value={childForm.shift}
                      onValueChange={(value) => setChildForm({ ...childForm, shift: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="day">Jour (06:00 - 18:00)</SelectItem>
                        <SelectItem value="night">Nuit (18:00 - 06:00)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Hours */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Heure début</Label>
                    <Input
                      type="time"
                      value={childForm.plannedStartTime}
                      onChange={(e) => setChildForm({ ...childForm, plannedStartTime: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Heure fin</Label>
                    <Input
                      type="time"
                      value={childForm.plannedEndTime}
                      onChange={(e) => setChildForm({ ...childForm, plannedEndTime: e.target.value })}
                    />
                  </div>
                </div>

                {/* Override Options */}
                <div className="space-y-3 pt-2 border-t">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="useParentVehicle"
                      checked={childForm.useParentVehicle}
                      onCheckedChange={(checked) => setChildForm({ ...childForm, useParentVehicle: checked })}
                    />
                    <label htmlFor="useParentVehicle" className="text-sm cursor-pointer">
                      Utiliser le véhicule de l'opération principale ({operation?.assignedVehicle?.plate || 'Non assigné'})
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="useParentDriver"
                      checked={childForm.useParentDriver}
                      onCheckedChange={(checked) => setChildForm({ ...childForm, useParentDriver: checked })}
                    />
                    <label htmlFor="useParentDriver" className="text-sm cursor-pointer">
                      Utiliser le chauffeur de l'opération principale ({operation?.assignedDriver?.name || 'Non assigné'})
                    </label>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddChildModal(false)}>
                  Annuler
                </Button>
                <Button 
                  onClick={handleCreateChild} 
                  disabled={creatingChild}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {creatingChild ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Créer le transfert
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
      
      {/* Dialog: Rétro-statut (retour depuis payée) */}
      <Dialog open={showRetroStatusDialog} onOpenChange={(open) => { setShowRetroStatusDialog(open); if (!open) { setCorrectionMotif(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Correction de statut</DialogTitle>
            <DialogDescription>
              Revenir de "{getStatusLabel('payee')}" à "{getStatusLabel(retroTarget)}". Cette action sera enregistrée dans l'historique des corrections.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Motif de correction <span className="text-red-500">*</span></Label>
              <Textarea 
                placeholder="Ex: Erreur de marquage, paiement non confirmé par la banque..."
                value={correctionMotif}
                onChange={e => setCorrectionMotif(e.target.value)}
                className="mt-1"
                data-testid="retro-status-motif"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 5 caractères</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRetroStatusDialog(false)}>Annuler</Button>
            <Button 
              onClick={handleRetroStatus} 
              disabled={savingCorrection || !correctionMotif || correctionMotif.trim().length < 5}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="retro-status-confirm"
            >
              {savingCorrection ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirmer la correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog: Édition détails financiers */}
      <Dialog open={showFinancialEditDialog} onOpenChange={(open) => { setShowFinancialEditDialog(open); if (!open) { setCorrectionMotif(''); setFinancialEdit({}); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier les détails financiers</DialogTitle>
            <DialogDescription>
              Opération {operation?.operationNumber}. Toute modification est tracée.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Montant facturé (FCFA)</Label>
                <Input 
                  type="number" 
                  defaultValue={operation?.estimatedPrice || 0}
                  onChange={e => setFinancialEdit(prev => ({...prev, estimatedPrice: parseFloat(e.target.value) || 0}))}
                  data-testid="financial-edit-amount"
                />
              </div>
              <div>
                <Label className="text-sm">Prix transporteur (FCFA)</Label>
                <Input 
                  type="number" 
                  defaultValue={operation?.carrierPrice || 0}
                  onChange={e => setFinancialEdit(prev => ({...prev, carrierPrice: parseFloat(e.target.value) || 0}))}
                  data-testid="financial-edit-carrier"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">Méthode de paiement</Label>
              <Select 
                defaultValue={operation?.paymentMethod || ''} 
                onValueChange={v => setFinancialEdit(prev => ({...prev, paymentMethod: v}))}
              >
                <SelectTrigger data-testid="financial-edit-method"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="virement">Virement bancaire</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="wave">Wave</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Référence de paiement</Label>
              <Input 
                defaultValue={operation?.paymentReference || ''}
                onChange={e => setFinancialEdit(prev => ({...prev, paymentReference: e.target.value}))}
                placeholder="Numéro de transaction, chèque..."
                data-testid="financial-edit-ref"
              />
            </div>
            <Separator />
            <div>
              <Label className="text-sm font-medium">Motif de modification <span className="text-red-500">*</span></Label>
              <Textarea 
                placeholder="Ex: Correction montant après négociation finale..."
                value={correctionMotif}
                onChange={e => setCorrectionMotif(e.target.value)}
                className="mt-1"
                data-testid="financial-edit-motif"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 5 caractères</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFinancialEditDialog(false)}>Annuler</Button>
            <Button 
              onClick={handleFinancialEdit} 
              disabled={savingCorrection || !correctionMotif || correctionMotif.trim().length < 5 || Object.keys(financialEdit).length === 0}
              data-testid="financial-edit-confirm"
            >
              {savingCorrection ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enregistrer les modifications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Historique des corrections financières */}
      <Dialog open={financialCorrections.length > 0 && showFinancialEditDialog === false && showRetroStatusDialog === false && false}>
        <DialogContent />
      </Dialog>

      {/* Cancel Operation Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Annuler l'opération</DialogTitle>
            <DialogDescription>
              Cette action changera le statut de l'opération en "Annulée". Veuillez fournir une raison.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="cancel-reason" className="text-sm font-medium">Raison de l'annulation *</Label>
            <Textarea
              id="cancel-reason"
              className="mt-2 min-h-[100px]"
              placeholder="Raison de l'annulation (obligatoire)..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              data-testid="cancel-reason-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)} data-testid="cancel-modal-close-btn">
              Fermer
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!cancelReason.trim()) {
                  toast({ title: "Erreur", description: "La raison est obligatoire.", variant: "destructive" });
                  return;
                }
                setShowCancelModal(false);
                handleStatusChange('annulee_admin', null, cancelReason.trim());
              }}
              disabled={!cancelReason.trim()}
              data-testid="cancel-modal-confirm-btn"
            >
              Confirmer l'annulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOperationDetail;
